import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { messageAPI, userAPI } from '../services/api';
import { useAuth } from '../contexts/FirebaseAuthContext';
import { Send, ArrowLeft, Plus, X, Search } from 'lucide-react';

const Messages = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { userId } = useParams();
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [showNewMessageModal, setShowNewMessageModal] = useState(false);
  // Item the buyer clicked "Contact Seller" from (passed via navigation state).
  // Used to show context in the header and attach to the first message sent.
  const [activeItem, setActiveItem] = useState(location.state?.item || null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Chat fills the space between the fixed header (70px) and the bottom area
  // (80px — mobile nav / footer gutter). dvh handles mobile browser chrome so
  // the message input is always visible, like a native texting app.
  const chatHeight = 'calc(100dvh - 150px)';

  // Route-driven screens (like any mobile chat app):
  //  - /messages            -> the conversation LIST screen
  //  - /messages/:userId    -> the CHAT screen (Back returns to the list)
  // On desktop both panes show side by side. On mobile only one shows at a time,
  // decided by the URL, so the browser/Back button behaves correctly.
  const inChat = !!userId;
  const showList = !isMobile || !inChat;
  const showChat = !isMobile || inChat;

  const containerStyle = {
    display: 'flex',
    height: chatHeight,
    maxWidth: isMobile ? '100%' : '900px',
    width: '100%',
    margin: isMobile ? 0 : '0 auto',
    backgroundColor: 'white',
    boxShadow: isMobile ? 'none' : '0 1px 3px rgba(0,0,0,0.1)',
    overflowX: 'hidden',
  };

  const goBackToList = () => {
    setSelectedConversation(null);
    navigate('/messages');
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const fetchConversations = async () => {
    try {
      setLoading(true);
      const response = await messageAPI.getConversations();
      setConversations(response.data.data || []);
    } catch (error) {
      console.error('Failed to fetch conversations:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchConversation = useCallback(async (otherUserId) => {
    try {
      const response = await messageAPI.getConversation(otherUserId);
      const conversation = response.data.data.conversation;
      setSelectedConversation(conversation);
      setMessages(response.data.data.messages || []);

      // Prefer the item already stored on the conversation; otherwise keep the
      // item passed in via navigation (fresh "Contact Seller" click).
      if (conversation?.item) {
        setActiveItem(conversation.item);
      } else if (location.state?.item) {
        setActiveItem(location.state.item);
      } else {
        setActiveItem(null);
      }
    } catch (error) {
      console.error('Failed to fetch conversation:', error);
    }
  }, [location.state?.item]);

  useEffect(() => {
    fetchConversations();
  }, []);

  useEffect(() => {
    if (userId) {
      fetchConversation(userId);
    }
  }, [userId, fetchConversation]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSelectConversation = (conversation) => {
    const otherUser = conversation.participants.find((p) => p._id !== user._id);
    navigate(`/messages/${otherUser._id}`);
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || sending) return;

    try {
      setSending(true);
      const otherUser = selectedConversation.participants.find((p) => p._id !== user._id);

      const response = await messageAPI.sendMessage({
        recipientId: otherUser._id,
        content: newMessage.trim(),
        itemId: activeItem?._id,
      });

      // Add new message to list
      setMessages((prev) => [...prev, response.data.data]);
      setNewMessage('');

      // Refresh conversations list to update lastMessage
      fetchConversations();
    } catch (error) {
      console.error('Failed to send message:', error);
      alert('Failed to send message. Please try again.');
    } finally {
      setSending(false);
    }
  };

  const formatDate = (date) => {
    const now = new Date();
    const msgDate = new Date(date);
    const diffMs = now - msgDate;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m`;
    if (diffHours < 24) return `${diffHours}h`;
    if (diffDays < 7) return `${diffDays}d`;
    return msgDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const formatMessageTime = (date) => {
    return new Date(date).toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  };

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: chatHeight,
      }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <>
      <div style={containerStyle}>
        {/* Conversations Sidebar (list screen) */}
        {showList && (
        <div
          style={{
            width: isMobile ? '100%' : '380px',
            maxWidth: '100%',
            display: 'flex',
            flexDirection: 'column',
            borderRight: isMobile ? 'none' : '1px solid #e0e0e0',
            backgroundColor: 'white',
          }}
        >
          {/* Sidebar Header */}
          <div style={{
            padding: '20px 24px',
            borderBottom: '1px solid #e0e0e0',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}>
            <h2 style={{ fontSize: '24px', fontWeight: '700', margin: 0 }}>💬 Messages</h2>
            <button
              onClick={() => setShowNewMessageModal(true)}
              className="btn btn-primary"
              style={{
                borderRadius: '50%',
                width: '40px',
                height: '40px',
                padding: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
              title="New Message"
            >
              <Plus size={20} />
            </button>
          </div>

          {/* Conversations List */}
          <div style={{ flex: 1, overflowY: 'auto' }}>
            {conversations.length === 0 ? (
              <div style={{
                textAlign: 'center',
                padding: '60px 24px',
                color: '#666',
              }}>
                <p style={{ fontSize: '16px', marginBottom: '8px' }}>📭 No conversations yet</p>
                <p style={{ fontSize: '14px', color: '#999' }}>
                  Start messaging sellers to begin chatting 👋
                </p>
              </div>
            ) : (
              conversations.map((conversation) => {
                const otherUser = conversation.participants.find((p) => p._id !== user._id);
                const isSelected = selectedConversation?._id === conversation._id;

                return (
                  <div
                    key={conversation._id}
                    onClick={() => handleSelectConversation(conversation)}
                    style={{
                      padding: '16px 24px',
                      cursor: 'pointer',
                      backgroundColor: isSelected ? '#f0f7ff' : 'transparent',
                      borderBottom: '1px solid #f0f0f0',
                      transition: 'background-color 0.15s',
                    }}
                    onMouseEnter={(e) => {
                      if (!isSelected) e.currentTarget.style.backgroundColor = '#f8f9fa';
                    }}
                    onMouseLeave={(e) => {
                      if (!isSelected) e.currentTarget.style.backgroundColor = 'transparent';
                    }}
                  >
                    <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
                      <img
                        src={
                          otherUser?.profileImage ||
                          `https://ui-avatars.com/api/?name=${otherUser?.username}&size=56&background=e60023&color=fff`
                        }
                        alt={otherUser?.username}
                        style={{
                          width: '56px',
                          height: '56px',
                          borderRadius: '50%',
                          objectFit: 'cover',
                          flexShrink: 0,
                        }}
                      />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          marginBottom: '4px',
                        }}>
                          <h4 style={{
                            fontSize: '15px',
                            fontWeight: '600',
                            margin: 0,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}>
                            {otherUser?.username}
                          </h4>
                          <span style={{ fontSize: '12px', color: '#999', flexShrink: 0, marginLeft: '8px' }}>
                            {formatDate(conversation.lastMessageAt || conversation.createdAt)}
                          </span>
                        </div>
                        {conversation.lastMessage && (
                          <p
                            style={{
                              fontSize: '14px',
                              color: '#666',
                              margin: 0,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              lineHeight: '1.4',
                            }}
                          >
                            {conversation.lastMessage.content}
                          </p>
                        )}
                        {conversation.item && (
                          <span
                            style={{
                              display: 'inline-block',
                              marginTop: '4px',
                              fontSize: '12px',
                              color: '#2196F3',
                              backgroundColor: '#e3f2fd',
                              padding: '2px 8px',
                              borderRadius: '10px',
                              maxWidth: '100%',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            🏷️ {conversation.item.title}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
        )}

        {/* Chat Area (chat screen) */}
        {showChat && (
        <div style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          backgroundColor: '#fafafa',
        }}>
          {selectedConversation ? (
            <>
              {/* Chat Header */}
              <div
                style={{
                  padding: '16px 24px',
                  borderBottom: '1px solid #e0e0e0',
                  backgroundColor: 'white',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                }}
              >
                {isMobile && (
                  <button
                    onClick={goBackToList}
                    aria-label="Back to messages"
                    style={{
                      padding: '8px',
                      marginRight: '4px',
                      border: 'none',
                      background: 'none',
                      color: '#333',
                      cursor: 'pointer',
                    }}
                  >
                    <ArrowLeft size={24} />
                  </button>
                )}
                {(() => {
                  const otherUser = selectedConversation.participants.find((p) => p._id !== user._id);
                  return (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1 }}>
                      <img
                        src={
                          otherUser?.profileImage ||
                          `https://ui-avatars.com/api/?name=${otherUser?.username}&size=48&background=e60023&color=fff`
                        }
                        alt={otherUser?.username}
                        onClick={() => navigate(`/profile/${otherUser?.username}`)}
                        style={{
                          width: '48px',
                          height: '48px',
                          borderRadius: '50%',
                          objectFit: 'cover',
                          cursor: 'pointer',
                        }}
                      />
                      <div>
                        <h3
                          onClick={() => navigate(`/profile/${otherUser?.username}`)}
                          style={{
                            fontSize: '16px',
                            fontWeight: '600',
                            margin: 0,
                            cursor: 'pointer',
                          }}
                        >
                          {otherUser?.username}
                        </h3>
                        <p style={{ fontSize: '13px', color: '#999', margin: 0 }}>
                          Active on Baby Resell
                        </p>
                      </div>
                    </div>
                  );
                })()}
              </div>

              {/* Item context banner - pinned at the top of the chat so the
                  listing this conversation is about is always one tap away */}
              {activeItem && (
                <div
                  onClick={() => navigate(`/items/${activeItem._id}`)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    padding: '10px 16px',
                    backgroundColor: '#f0f7ff',
                    borderBottom: '1px solid #cfe3ff',
                    cursor: 'pointer',
                    position: 'sticky',
                    top: 0,
                    zIndex: 5,
                  }}
                  title="View listing"
                >
                  <img
                    src={
                      activeItem.images?.[0]?.thumbnail ||
                      activeItem.images?.[0]?.fullSize ||
                      '/placeholder.png'
                    }
                    alt={activeItem.title}
                    style={{
                      width: '44px',
                      height: '44px',
                      borderRadius: '8px',
                      objectFit: 'cover',
                      flexShrink: 0,
                    }}
                  />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: '12px', color: '#666' }}>Regarding listing</div>
                    <div
                      style={{
                        fontSize: '14px',
                        fontWeight: 600,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {activeItem.title}
                    </div>
                  </div>
                  {activeItem.price != null && (
                    <div style={{ fontSize: '15px', fontWeight: 700, color: '#4CAF50' }}>
                      ${activeItem.price}
                    </div>
                  )}
                  <span style={{ color: 'var(--primary)', fontWeight: 600, fontSize: '13px', whiteSpace: 'nowrap' }}>
                    View ›
                  </span>
                </div>
              )}

              {/* Messages Area */}
              <div
                style={{
                  flex: 1,
                  overflowY: 'auto',
                  padding: isMobile ? '12px 12px 16px' : '24px',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: isMobile ? '8px' : '12px',
                }}
              >
                {messages.length === 0 ? (
                  <div style={{
                    flex: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#999',
                  }}>
                    <div style={{ textAlign: 'center' }}>
                      <p style={{ fontSize: '16px', marginBottom: '8px' }}>No messages yet</p>
                      <p style={{ fontSize: '14px' }}>Start the conversation!</p>
                    </div>
                  </div>
                ) : (
                  messages.map((message, index) => {
                    const isOwn = message.sender._id === user._id;
                    const showAvatar = !isOwn && (index === 0 || messages[index - 1].sender._id !== message.sender._id);

                    return (
                      <div
                        key={message._id}
                        style={{
                          display: 'flex',
                          justifyContent: isOwn ? 'flex-end' : 'flex-start',
                          gap: '8px',
                          alignItems: 'flex-end',
                        }}
                      >
                        {!isOwn && (
                          <div style={{ width: '32px', height: '32px', flexShrink: 0 }}>
                            {showAvatar && (
                              <img
                                src={
                                  message.sender.profileImage ||
                                  `https://ui-avatars.com/api/?name=${message.sender.username}&size=32&background=e60023&color=fff`
                                }
                                alt={message.sender.username}
                                style={{
                                  width: '32px',
                                  height: '32px',
                                  borderRadius: '50%',
                                  objectFit: 'cover',
                                }}
                              />
                            )}
                          </div>
                        )}
                        <div
                          style={{
                            maxWidth: isMobile ? '80%' : '65%',
                            padding: '10px 16px',
                            borderRadius: isOwn ? '20px 20px 4px 20px' : '20px 20px 20px 4px',
                            backgroundColor: isOwn ? '#2196F3' : 'white',
                            color: isOwn ? 'white' : '#333',
                            boxShadow: '0 1px 2px rgba(0,0,0,0.08)',
                          }}
                        >
                          <p style={{
                            margin: 0,
                            wordWrap: 'break-word',
                            fontSize: '15px',
                            lineHeight: '1.4',
                          }}>
                            {message.content}
                          </p>
                          <span
                            style={{
                              fontSize: '11px',
                              opacity: 0.8,
                              marginTop: '4px',
                              display: 'block',
                            }}
                          >
                            {formatMessageTime(message.createdAt)}
                          </span>
                        </div>
                      </div>
                    );
                  })
                )}
                <div ref={messagesEndRef} />
              </div>

              {/* Message Input */}
              <form
                onSubmit={handleSendMessage}
                style={{
                  padding: '16px 24px',
                  borderTop: '1px solid #e0e0e0',
                  backgroundColor: 'white',
                  display: 'flex',
                  gap: '12px',
                  alignItems: 'center',
                }}
              >
                <input
                  type="text"
                  placeholder="Type a message..."
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  disabled={sending}
                  style={{
                    flex: 1,
                    padding: '12px 20px',
                    borderRadius: '24px',
                    border: '1px solid #e0e0e0',
                    fontSize: '15px',
                    outline: 'none',
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#2196F3'}
                  onBlur={(e) => e.target.style.borderColor = '#e0e0e0'}
                />
                <button
                  type="submit"
                  disabled={!newMessage.trim() || sending}
                  style={{
                    width: '44px',
                    height: '44px',
                    borderRadius: '50%',
                    border: 'none',
                    backgroundColor: newMessage.trim() ? '#2196F3' : '#e0e0e0',
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: newMessage.trim() ? 'pointer' : 'not-allowed',
                    transition: 'background-color 0.2s',
                  }}
                >
                  {sending ? (
                    <div className="spinner-border spinner-border-sm" style={{ width: '16px', height: '16px' }} />
                  ) : (
                    <Send size={18} />
                  )}
                </button>
              </form>
            </>
          ) : (
            <div
              style={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#999',
              }}
            >
              <div style={{ textAlign: 'center' }}>
                <p style={{ fontSize: '18px', marginBottom: '8px' }}>Select a conversation</p>
                <p style={{ fontSize: '14px' }}>Choose from your existing conversations or start a new one</p>
              </div>
            </div>
          )}
        </div>
        )}
      </div>

      {/* New Message Modal */}
      {showNewMessageModal && (
        <NewMessageModal
          user={user}
          onClose={() => setShowNewMessageModal(false)}
          onSelectUser={(userId) => {
            setShowNewMessageModal(false);
            navigate(`/messages/${userId}`);
          }}
        />
      )}
    </>
  );
};

// New Message Modal Component
const NewMessageModal = ({ user, onClose, onSelectUser }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [following, setFollowing] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('following'); // 'following' or 'search'

  useEffect(() => {
    fetchFollowing();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchFollowing = async () => {
    try {
      const response = await userAPI.getFollowing(user._id);
      setFollowing(response.data.data || []);
    } catch (error) {
      console.error('Failed to fetch following:', error);
    }
  };

  const handleSearch = async (query) => {
    if (!query.trim()) {
      setSearchResults([]);
      return;
    }

    try {
      setLoading(true);
      const response = await userAPI.getUserByUsername(query.trim());
      if (response.data.data && response.data.data._id !== user._id) {
        setSearchResults([response.data.data]);
      } else {
        setSearchResults([]);
      }
    } catch (error) {
      console.error('Failed to search users:', error);
      setSearchResults([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'search') {
      const timer = setTimeout(() => {
        handleSearch(searchQuery);
      }, 300);
      return () => clearTimeout(timer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery, activeTab]);

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        padding: '20px',
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          backgroundColor: 'white',
          borderRadius: '16px',
          width: '100%',
          maxWidth: '500px',
          maxHeight: '600px',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
        }}
      >
        {/* Modal Header */}
        <div style={{
          padding: '20px 24px',
          borderBottom: '1px solid #e0e0e0',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}>
          <h3 style={{ fontSize: '20px', fontWeight: '700', margin: 0 }}>New Message</h3>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '4px',
              color: '#666',
            }}
          >
            <X size={24} />
          </button>
        </div>

        {/* Tabs */}
        <div style={{
          display: 'flex',
          borderBottom: '1px solid #e0e0e0',
        }}>
          <button
            onClick={() => setActiveTab('following')}
            style={{
              flex: 1,
              padding: '12px',
              border: 'none',
              background: 'none',
              fontSize: '15px',
              fontWeight: '600',
              color: activeTab === 'following' ? '#2196F3' : '#666',
              borderBottom: activeTab === 'following' ? '2px solid #2196F3' : 'none',
              cursor: 'pointer',
            }}
          >
            Following ({following.length})
          </button>
          <button
            onClick={() => setActiveTab('search')}
            style={{
              flex: 1,
              padding: '12px',
              border: 'none',
              background: 'none',
              fontSize: '15px',
              fontWeight: '600',
              color: activeTab === 'search' ? '#2196F3' : '#666',
              borderBottom: activeTab === 'search' ? '2px solid #2196F3' : 'none',
              cursor: 'pointer',
            }}
          >
            Search
          </button>
        </div>

        {/* Search Input (for search tab) */}
        {activeTab === 'search' && (
          <div style={{ padding: '16px 24px', borderBottom: '1px solid #f0f0f0' }}>
            <div style={{ position: 'relative' }}>
              <Search
                size={20}
                style={{
                  position: 'absolute',
                  left: '12px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: '#999',
                }}
              />
              <input
                type="text"
                placeholder="Search by username..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px 12px 10px 40px',
                  border: '1px solid #e0e0e0',
                  borderRadius: '8px',
                  fontSize: '15px',
                  outline: 'none',
                }}
                onFocus={(e) => e.target.style.borderColor = '#2196F3'}
                onBlur={(e) => e.target.style.borderColor = '#e0e0e0'}
              />
            </div>
          </div>
        )}

        {/* User List */}
        <div style={{ flex: 1, overflowY: 'auto' }}>
          {activeTab === 'following' ? (
            following.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '40px 20px', color: '#999' }}>
                <p>You're not following anyone yet</p>
              </div>
            ) : (
              following.map((followedUser) => (
                <UserItem
                  key={followedUser._id}
                  user={followedUser}
                  onClick={() => onSelectUser(followedUser._id)}
                />
              ))
            )
          ) : (
            <>
              {loading ? (
                <div style={{ textAlign: 'center', padding: '40px 20px' }}>
                  <div className="spinner-border text-primary" />
                </div>
              ) : searchQuery.trim() === '' ? (
                <div style={{ textAlign: 'center', padding: '40px 20px', color: '#999' }}>
                  <p>Enter a username to search</p>
                </div>
              ) : searchResults.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '40px 20px', color: '#999' }}>
                  <p>No users found</p>
                </div>
              ) : (
                searchResults.map((searchedUser) => (
                  <UserItem
                    key={searchedUser._id}
                    user={searchedUser}
                    onClick={() => onSelectUser(searchedUser._id)}
                  />
                ))
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

// User Item Component for Modal
const UserItem = ({ user, onClick }) => (
  <div
    onClick={onClick}
    style={{
      padding: '12px 24px',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      transition: 'background-color 0.15s',
    }}
    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f8f9fa'}
    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
  >
    <img
      src={
        user.profileImage ||
        `https://ui-avatars.com/api/?name=${user.username}&size=48&background=e60023&color=fff`
      }
      alt={user.username}
      style={{
        width: '48px',
        height: '48px',
        borderRadius: '50%',
        objectFit: 'cover',
      }}
    />
    <div>
      <h4 style={{ fontSize: '15px', fontWeight: '600', margin: 0 }}>{user.username}</h4>
      {user.email && (
        <p style={{ fontSize: '13px', color: '#999', margin: 0 }}>{user.email}</p>
      )}
    </div>
  </div>
);

export default Messages;
